import { useEffect, useState } from "react";
import Tooltip from "../../../Components/Tooltip/index.js";
import { BoxTitle } from "./Overview.js";
import Box from "../Box.js";
import useApi from "../../../Hooks/useApi.js";
import { isAuthenticated } from "../../../Helpers/authHelper.js";
import {
  fetchIMISIndicatorsList,
  fetchIndicatorsList,
} from "../../../Utils/imisCityDashboardUtils.js";
import LoadingSpinner from "../../Shared/LoadingSpinner.js";
import { NoDataBox } from "../../UI/Charts/NoDataFoundMessage.js";

// --- Sub-Components ---

// 2. Grid for Boxes
const IndicatorsInfos = ({ indicators }) => {
  if (!indicators || indicators.length === 0) return <NoDataBox />;

  return (
    <div
      className="w-full grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4"
      style={{ color: "#2F4B7C" }}
    >
      {indicators.map((i, idx) => (
        <Box
          key={idx}
          value={i.value}
          unit={i.unit ? i.unit : ""}
          text={i.name}
          icon={i.icon}
          textsize="17px"
          color={i.color}
        />
      ))}
    </div>
  );
};

// 3. Reusable Column Logic (Handles both First & Second City)
const CityColumn = ({ cityData, indicatorsData, loading }) => {
  if (loading) return <LoadingSpinner message="Loading Data..." />;

  // Case 1: No Data Source at all
  if (!cityData?.dataSource?.length && !cityData?.imis) {
    return <NoDataBox message="No Data Found" />;
  }

  // Case 2: IMIS Data
  if (cityData?.imis) {
    const hasImisData =
      indicatorsData && Object.keys(indicatorsData).length > 0;

    if (!hasImisData) return <NoDataBox />;

    return (
      <div className="space-y-8">
        {Object.entries(indicatorsData).map(
          ([dataItemName, dataItemValues], index) => (
            <div key={index}>
              {index > 0 && <hr className="my-5 border-gray-200" />}
              <p className="font-semibold text-base text-start text-[#2F4B7C] mb-4 capitalize">
                {dataItemName}
              </p>
              <IndicatorsInfos indicators={dataItemValues} />
            </div>
          )
        )}
      </div>
    );
  }

  // Case 3: Standard Data (CWIS & SWM)
  // Prepare data with colors
  const cwisData =
    indicatorsData?.CWIS?.map((item) => ({ ...item, color: "#2F4B7C" })) || [];
  const swmData =
    indicatorsData?.["Solid Waste Management (SWM)"]?.map((item) => ({
      ...item,
      color: "#FFA600",
    })) || [];


  const hasAnyData = cwisData.length > 0 || swmData.length > 0;

  if (!hasAnyData) return <NoDataBox />;

  return (
    <div className="space-y-8">
      {/* CWIS Section */}
      <div>
        <div className="border-b border-gray-200 pb-2 mb-4 mt-2">
          <p className="text-base text-start text-[#2F4B7C]">
            <Tooltip id={"CWIS"}>CWIS</Tooltip>
          </p>
        </div>
        <IndicatorsInfos indicators={cwisData} />
      </div>

      {/* SWM Section */}
      <div>
        <div className="border-b border-gray-200 pb-2 mb-4">
          <p className="text-base text-start text-[#2F4B7C]">
            Solid Waste Management (SWM)
          </p>
        </div>
        <IndicatorsInfos indicators={swmData} />
      </div>
    </div>
  );
};

// --- Main Component ---

const Indicators = ({ isComparison, firstCity, secondCity }) => {
  const { request: requestPrivate, loading: loadingFirst } = useApi({
    isPublic: !isAuthenticated(),
  });
  const { request: requestSecond, loading: loadingSecond } = useApi({
    isPublic: !isAuthenticated(),
  });

  const [firstIndicatorsData, setFirstIndicatorsData] = useState({});
  const [secondIndicatorsData, setSecondIndicatorsData] = useState({});

  // Helper to construct city object
  const getCityObj = (cityProp) => ({
    city: cityProp?.city?.name,
    city_id: cityProp?.city?.id,
    year: cityProp?.year,
    dataSource: cityProp?.dataSource,
    imis: cityProp?.dataSource?.find((item) => item.group === "IMIS")
      ? true
      : false,
  });

  const firstCityData = getCityObj(firstCity);
  const secondCityData = getCityObj(secondCity);

  // Fetch Logic
  useEffect(() => {
    if (!firstCityData.city || !firstCityData.year) return;
    const fetchIndicators = async () => {
      const payload = {
        city: firstCityData.imis ? firstCityData.city : firstCityData.city_id,
        year: firstCityData.year,
      };
      const fetcher = firstCityData.imis
        ? fetchIMISIndicatorsList
        : fetchIndicatorsList;
      await fetcher(requestPrivate, payload, setFirstIndicatorsData);
    };
    fetchIndicators();
  }, [firstCityData.city, firstCityData.year, firstCityData.imis, requestPrivate]);

  useEffect(() => {
    if (!secondCityData.city || !secondCityData.year) return;
    const fetchIndicators = async () => {
      const payload = {
        city: secondCityData.imis
          ? secondCityData.city
          : secondCityData.city_id,
        year: secondCityData.year,
      };
      const fetcher = secondCityData.imis
        ? fetchIMISIndicatorsList
        : fetchIndicatorsList;
      await fetcher(requestSecond, payload, setSecondIndicatorsData);
    };
    fetchIndicators();
  }, [secondCityData.city, secondCityData.year, secondCityData.imis, requestSecond]);

  return (
    <div
      id="Indicators"
      className="border border-[#2F4B7C] border-opacity-30 p-5 rounded-lg bg-white"
    >
      <BoxTitle title={"Indicators"} />

      <div
        className={`grid gap-8 ${
          isComparison ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
        }`}
      >
        {/* First City Column */}
        <div
          className={
            isComparison
              ? "w-full border-r-0 lg:border-r border-gray-200 lg:pr-8"
              : "w-full"
          }
        >
          <CityColumn
            cityData={firstCityData}
            indicatorsData={firstIndicatorsData}
            loading={loadingFirst}
          />
        </div>

        {/* Second City Column */}
        {isComparison && (
          <div className="w-full">
            <CityColumn
              cityData={secondCityData}
              indicatorsData={secondIndicatorsData}
              loading={loadingSecond}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Indicators;
